import { useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  SparklesIcon,
  TrashIcon
} from "@heroicons/react/24/outline";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../utils/cn.js";
import { DropdownMenu } from "./DropdownMenu.js";
import { Dialog } from "./Dialog.js";
import { ShadButton } from "./Button.js";
import { downloadBlob, getFileExtension, isImageFile } from "../utils/index.js";
import ImageZoom from "./ImageZoom.js";
import { Tooltip } from "./Tooltip.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";
import { Spinner } from "./Spinner.js";

const GUMMY_RAISED_CARD = cn(
  "rounded-surface-md bg-white dark:bg-mono-900",
  "shadow-card"
);

type FileData = {
  id: number | string;
  url?: string | null;
  thumbnail_url?: string | null;
  name?: string | null;
  description?: string | null;
  size_bytes?: number;
  created_at?: string;
  creator?: string | null;
  has_permission?: boolean | null;
  can_analyze?: boolean;
  source?: "swivu" | "companycam";
  external_id?: string | null;
};

type FileCardProps = {
  file: FileData;
  className?: string;
  onDelete?: (id: number | string) => void;
  onAnalyze?: (file: FileData) => void;
  isDeleting?: boolean;
  advisorsEnabled?: boolean;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileIcon = ({
  name,
  isGummy
}: {
  name?: string | null;
  isGummy?: boolean;
}) => {
  const cls = isGummy ? "size-10" : "size-8";
  const stroke = isGummy ? 2.5 : undefined;
  if (isImageFile(name)) {
    return <PhotoIcon className={cls} strokeWidth={stroke} />;
  }
  return <DocumentTextIcon className={cls} strokeWidth={stroke} />;
};

const ActionsMenu = ({
  position,
  canDownload,
  canAnalyze,
  canDelete,
  onDownload,
  onOpenInTab,
  onAnalyze,
  onDeleteClick
}: {
  position: string;
  canDownload: boolean;
  canAnalyze: boolean;
  canDelete: boolean;
  onDownload?: () => void;
  onOpenInTab?: () => void;
  onAnalyze?: () => void;
  onDeleteClick: () => void;
}) => {
  const isGummy = useIsGummy();
  return (
    <div className={cn("absolute z-10", position)}>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            onClick={(e) => e.preventDefault()}
            aria-label="More options"
            className={cn(
              isGummy
                ? cn(
                    "mobile-pref-control-tight",
                    "inline-flex size-12 items-center justify-center rounded-full",
                    "bg-white text-mono-900 dark:bg-mono-800 dark:text-white",
                    "shadow-raised",
                    "transition-transform duration-150",
                    "hover:-translate-y-0.5 active:scale-[0.96]",
                    "data-[state=open]:-translate-y-0.5",
                    "motion-reduce:transform-none motion-reduce:transition-none",
                    "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40"
                  )
                : "bg-background/80 hover:bg-background border border-border text-foreground flex h-7 w-7 items-center justify-center rounded-lg backdrop-blur-sm transition-colors"
            )}
          >
            <EllipsisVerticalIcon
              className={cn(
                "mobile-pref-control-icon",
                isGummy ? "size-7" : "size-5"
              )}
              strokeWidth={isGummy ? 3 : undefined}
            />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end">
          {canDownload && (
            <DropdownMenu.Item
              className="mobile-pref-menu-item"
              onSelect={onDownload}
            >
              <DocumentArrowDownIcon
                className={cn("mr-2", isGummy ? "size-5" : "size-4")}
              />
              Download
            </DropdownMenu.Item>
          )}
          {canDownload && (
            <DropdownMenu.Item
              className="mobile-pref-menu-item"
              onSelect={onOpenInTab}
            >
              <ArrowTopRightOnSquareIcon
                className={cn("mr-2", isGummy ? "size-5" : "size-4")}
              />
              Open in Tab
            </DropdownMenu.Item>
          )}
          {canAnalyze && (
            <DropdownMenu.Item
              className="mobile-pref-menu-item"
              onSelect={onAnalyze}
            >
              <SparklesIcon
                className={cn("mr-2", isGummy ? "size-5" : "size-4")}
              />
              Analyze
            </DropdownMenu.Item>
          )}
          {canDelete && (canDownload || canAnalyze) && (
            <DropdownMenu.Separator />
          )}
          {canDelete && (
            <DropdownMenu.Item
              onSelect={onDeleteClick}
              className="mobile-pref-menu-item text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
            >
              <TrashIcon
                className={cn("mr-2", isGummy ? "size-5" : "size-4")}
              />
              Delete
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
};

const DeleteDialog = ({
  open,
  onOpenChange,
  isImage,
  fileName,
  isDeleting,
  onConfirm
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isImage: boolean;
  fileName: string;
  isDeleting?: boolean;
  onConfirm: () => void;
}) => {
  const isGummy = useIsGummy();
  const title = `Delete ${isImage ? "photo" : "file"}`;
  return (
    <Dialog.Root modal={true} open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-md" position="center">
        <Dialog.Header>
          <div className="flex items-start gap-4">
            {isGummy ? (
              <span
                className={cn(
                  "flex size-14 shrink-0 items-center justify-center rounded-full text-white",
                  "bg-tile-alert shadow-raised"
                )}
                style={{ textShadow: "var(--text-shadow-on-color-strong)" }}
              >
                <ExclamationTriangleIcon className="size-7" strokeWidth={2.5} />
              </span>
            ) : (
              <div>
                <ExclamationTriangleIcon className="size-8 text-destructive" />
              </div>
            )}
            <div className="min-w-0 flex-1 text-left">
              <Dialog.Title asChild={isGummy}>
                {isGummy ? (
                  <h2 className="text-2xl font-black leading-tight tracking-tight text-mono-900 dark:text-white">
                    {title}
                  </h2>
                ) : (
                  <>Delete {isImage ? "Photo" : "File"}</>
                )}
              </Dialog.Title>
              <Dialog.Description
                className={cn(
                  "py-3",
                  isGummy &&
                    "text-base font-bold text-mono-700 dark:text-mono-200"
                )}
              >
                Are you sure you want to delete "
                <span
                  className={cn(
                    "inline-block max-w-48 truncate align-bottom",
                    isGummy
                      ? "font-black text-mono-900 dark:text-white"
                      : "font-medium"
                  )}
                >
                  {fileName}
                </span>
                "? This action cannot be undone.
              </Dialog.Description>
            </div>
          </div>
        </Dialog.Header>
        <Dialog.Footer className="gap-3 pb-2 md:gap-4 md:pb-0">
          {isGummy ? (
            <>
              <DialogPrimitive.Close asChild>
                <button
                  type="button"
                  disabled={isDeleting}
                  className={cn(
                    "mobile-pref-control",
                    "inline-flex h-14 items-center justify-center rounded-surface-md px-6 text-lg font-black",
                    "bg-tile-neutral text-mono-900 shadow-raised",
                    "dark:from-mono-800 dark:to-mono-900 dark:text-white",
                    "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                    "motion-reduce:transform-none motion-reduce:transition-none",
                    "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
                    "disabled:pointer-events-none disabled:opacity-50",
                    "w-full md:w-auto md:flex-none"
                  )}
                >
                  Cancel
                </button>
              </DialogPrimitive.Close>
              <button
                type="button"
                onClick={onConfirm}
                disabled={isDeleting}
                className={cn(
                  "mobile-pref-control",
                  "inline-flex h-14 items-center justify-center gap-2 rounded-surface-md px-6 text-lg font-black",
                  "bg-tile-alert text-white shadow-raised",
                  "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                  "motion-reduce:transform-none motion-reduce:transition-none",
                  "outline-none focus-visible:ring-4 focus-visible:ring-white/80",
                  "disabled:pointer-events-none disabled:opacity-50 disabled:grayscale",
                  isDeleting && "cursor-wait",
                  "w-full md:w-auto md:flex-none"
                )}
                style={{ textShadow: "var(--text-shadow-on-color-strong)" }}
              >
                {isDeleting ? <Spinner className="size-6" /> : "Delete"}
              </button>
            </>
          ) : (
            <>
              <DialogPrimitive.Close asChild>
                <ShadButton variant="outline" disabled={isDeleting}>
                  Cancel
                </ShadButton>
              </DialogPrimitive.Close>
              <ShadButton
                variant="destructive"
                onClick={onConfirm}
                disabled={isDeleting}
                isLoading={isDeleting}
              >
                Delete
              </ShadButton>
            </>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const FileCard = ({
  file,
  className,
  onDelete,
  onAnalyze,
  isDeleting,
  advisorsEnabled
}: FileCardProps) => {
  const isGummy = useIsGummy();
  const canAnalyze =
    !!advisorsEnabled &&
    isImageFile(file.name) &&
    !!onAnalyze &&
    file.can_analyze !== false;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const hasImage = file.thumbnail_url || (file.url && isImageFile(file.name));
  const imageUrl = file.thumbnail_url || file.url;
  const ext = getFileExtension(file.name);
  const isCompanyCamFile = file.source === "companycam";
  const canDelete = !isCompanyCamFile && !!file.has_permission && !!onDelete;
  const canDownload = !!file.url;
  const hasActions = canDelete || canAnalyze || canDownload;
  const fileName = file.description || file.name || "Untitled";
  const isImage = isImageFile(file.name);

  const handleConfirmDelete = () => {
    onDelete?.(file.id);
    setShowDeleteDialog(false);
  };

  const handleDownload = () => {
    if (file.url) void downloadBlob(file.url, file.name || "download");
  };

  const handleOpenInTab = () => {
    if (file.url) window.open(file.url, "_blank");
  };

  // Image files use their intrinsic aspect ratio in gallery layouts.
  if (isImage && hasImage && imageUrl && file.url) {
    return (
      <>
        <div
          className={cn(
            "group relative overflow-hidden transition-all",
            isGummy
              ? cn(
                  GUMMY_RAISED_CARD,
                  "transition-transform duration-150 hover:-translate-y-0.5"
                )
              : "bg-card border-border rounded-xl border",
            isDeleting && "pointer-events-none opacity-50",
            className
          )}
        >
          {isCompanyCamFile && (
            <span
              className={cn(
                "absolute left-2 top-2 z-10 rounded-full bg-sky-700 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-md",
                isGummy && "text-xs font-black"
              )}
              title="Imported from CompanyCam"
            >
              CompanyCam
            </span>
          )}
          {hasActions && (
            <ActionsMenu
              position={isGummy ? "right-2 top-2" : "right-1 top-1"}
              canDownload={canDownload}
              canAnalyze={canAnalyze}
              canDelete={canDelete}
              onDownload={handleDownload}
              onOpenInTab={handleOpenInTab}
              onAnalyze={() => onAnalyze?.(file)}
              onDeleteClick={() => setShowDeleteDialog(true)}
            />
          )}
          {isGummy ? (
            <ImageZoom
              src={file.url}
              buttonClassName="relative block aspect-square w-full overflow-hidden rounded-t-[18px] bg-mono-100 dark:bg-mono-800"
            >
              <img
                src={imageUrl}
                alt={file.name || "File thumbnail"}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </ImageZoom>
          ) : (
            <ImageZoom src={file.url}>
              <img
                src={imageUrl}
                alt={file.name || "File thumbnail"}
                className="block w-full h-auto bg-mono-100 dark:bg-mono-900 rounded-t-md"
              />
            </ImageZoom>
          )}
          <div
            className={cn(
              "flex min-w-0 flex-col gap-1",
              isGummy ? "px-4 py-3" : "p-2.5"
            )}
          >
            {isGummy ? (
              <Tooltip.Provider delayDuration={200}>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <h5 className="mobile-pref-card-title cursor-default truncate text-left text-base font-black text-mono-900 dark:text-white">
                      {fileName}
                    </h5>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      side="top"
                      collisionPadding={8}
                      className="max-w-sm break-all"
                    >
                      {fileName}
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            ) : (
              <h5 className="mobile-pref-card-title line-clamp-2 break-all text-mono-900 dark:text-mono-100 text-xs font-medium">
                {fileName}
              </h5>
            )}
            <p
              className={cn(
                "mobile-pref-card-meta truncate",
                isGummy
                  ? "text-xs font-black uppercase tracking-widest text-mono-600 dark:text-mono-300"
                  : "text-mono-500 dark:text-mono-400 text-xs"
              )}
            >
              {file.size_bytes ? formatFileSize(file.size_bytes) : file.name}
            </p>
          </div>
        </div>
        <DeleteDialog
          open={showDeleteDialog}
          onOpenChange={(open) => {
            if (!open) setShowDeleteDialog(false);
          }}
          isImage={isImage}
          fileName={fileName}
          isDeleting={isDeleting}
          onConfirm={handleConfirmDelete}
        />
      </>
    );
  }

  // Non-image files: card layout with fixed aspect ratio
  return (
    <>
      <div
        className={cn(
          "group relative flex flex-row overflow-hidden transition-all sm:flex-col md:pr-0",
          isGummy ? "pr-14" : "pr-10",
          isGummy
            ? cn(
                GUMMY_RAISED_CARD,
                "transition-transform duration-150 hover:-translate-y-0.5"
              )
            : "bg-card border-border rounded-xl border",
          isDeleting && "pointer-events-none opacity-50",
          className
        )}
      >
        {hasActions && (
          <ActionsMenu
            position="right-2 top-2"
            canDownload={canDownload}
            canAnalyze={canAnalyze}
            canDelete={canDelete}
            onDownload={handleDownload}
            onOpenInTab={handleOpenInTab}
            onAnalyze={() => onAnalyze?.(file)}
            onDeleteClick={() => setShowDeleteDialog(true)}
          />
        )}
        <a
          href={file.url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "flex min-w-0 flex-row overflow-hidden sm:flex-1 sm:flex-col",
            !isGummy &&
              "hover:border-mono-300 dark:hover:border-mono-600 dark:hover:bg-mono-800 hover:shadow-md",
            !file.url && "pointer-events-none opacity-50"
          )}
        >
          <div
            className={cn(
              "relative flex shrink-0 items-center justify-center overflow-hidden sm:aspect-video sm:h-auto sm:w-full",
              isGummy
                ? "h-24 w-24 bg-mono-100 text-mono-700 dark:bg-mono-950 dark:text-mono-200"
                : "h-16 w-16 bg-mono-50 text-mono-300 dark:bg-mono-950 dark:text-mono-600"
            )}
          >
            {hasImage && imageUrl ? (
              <>
                <img
                  src={imageUrl}
                  alt={file.name || "File thumbnail"}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const sibling = e.currentTarget.nextElementSibling;
                    if (sibling) sibling.classList.remove("hidden");
                  }}
                />
                <div className="hidden">
                  <FileIcon name={file.name} isGummy={isGummy} />
                </div>
              </>
            ) : (
              <FileIcon name={file.name} isGummy={isGummy} />
            )}
            {ext && (
              <div
                className={cn(
                  "absolute uppercase",
                  isGummy
                    ? "bottom-2 right-2 px-2 py-1 text-xs rounded-full font-black text-mono-800 dark:text-mono-100 bg-white dark:bg-mono-800 shadow-raised"
                    : "bottom-1 right-1 backdrop-blur-sm px-1 py-0.5 text-[9px] sm:bottom-2 sm:right-2 sm:px-1.5 sm:text-[10px] bg-background/80 text-foreground border border-border rounded font-medium"
                )}
              >
                {ext}
              </div>
            )}
          </div>
        </a>
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-col justify-center gap-1 overflow-hidden",
            isGummy ? "p-5" : "p-2.5"
          )}
        >
          {isCompanyCamFile && (
            <span
              className={cn(
                "mb-1 w-fit rounded-full bg-sky-700 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white",
                isGummy && "text-xs font-black"
              )}
              title="Imported from CompanyCam"
            >
              CompanyCam
            </span>
          )}
          <h5
            className={cn(
              "mobile-pref-card-title line-clamp-2 break-all",
              isGummy
                ? "text-lg font-black text-mono-900 dark:text-white"
                : "text-mono-900 dark:text-mono-100 text-xs font-medium"
            )}
          >
            {fileName}
          </h5>
          <p
            className={cn(
              "mobile-pref-card-meta truncate",
              isGummy
                ? "text-xs font-black uppercase tracking-widest text-mono-600 dark:text-mono-300"
                : "text-mono-500 dark:text-mono-400 text-xs"
            )}
          >
            {file.size_bytes ? formatFileSize(file.size_bytes) : file.name}
          </p>
        </div>
      </div>
      <DeleteDialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          if (!open) setShowDeleteDialog(false);
        }}
        isImage={isImage}
        fileName={fileName}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

const FileCardSkeleton = () => (
  <div className="border-mono-200 dark:border-mono-700/50 dark:bg-mono-800/50 flex animate-pulse flex-row overflow-hidden rounded-xl border bg-white sm:flex-col">
    <div className="bg-mono-100 dark:bg-mono-700 h-16 w-16 shrink-0 sm:aspect-square sm:h-auto sm:w-full" />
    <div className="flex flex-1 flex-col justify-center space-y-1.5 p-2.5">
      <div className="bg-mono-200 dark:bg-mono-700 h-4 w-3/4 rounded" />
      <div className="bg-mono-200 dark:bg-mono-700 h-3 w-1/2 rounded" />
    </div>
  </div>
);

export { FileCard, FileCardSkeleton };
export type { FileData, FileCardProps };
